import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import State from "../../../../../context";
import { notification } from "antd";
import Overlay from "../../../../../atom/Overlay";
import UI from "../../../../../provider/UI";

import wealthAdvisorThemeBX from "../../../../bx/theme/manager";
import wealthAdvisorThemeDefault from "../theme";

import {
  getErrorMessageCopy,
  getManagerPendoAccountId,
  getManagerPendoVisitorMetadata,
  initializePendo,
  isOrganizationBx,
  setTenantSpecificHeadAttributes,
} from "../../../../../utils/helper/specialized";
import {
  editLead,
  getUserManagerData,
} from "../../../../../utils/request/manager";
import { getUserMetadata } from "../../super_admin/request";
import { getModelsSaved, getModelsSelected } from "../../admin/request";
import { getMassagedListEtf } from "../../super_admin/helper";

const defaultState = {
  compareProducts: [],
  investmentAssumptions: {
    advisorFee: "1%",
    annuities: 0,
    annuities_isOn: false,
    birthYear: 1987,
    contributionsIsOn: true,
    contributions: 12000,
    currentSpouseAge: 0,
    enableMonthlyContribution: true,
    householdIncome: 100000,
    incomeChartType: "contributions",
    inflationRate: 0,
    investmentAmount: 100000,
    investmentDuration: 20,
    lengthOfInvestment: 20,
    investmentObjective: "income",
    otherIncome: 0,
    otherIncome_isOn: false,
    socialSecurity: 0,
    socialSecurity_isOn: false,
    yearToStartWithdrawals: 5,
    withdrawalLevel: "4%",
  },
  pennyMessages: [],
  productsList: [],
  showOverlay: true,
  isManager: true,
};

const StateProvider = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const [state, setState] = useState(defaultState);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const emailSearchParam = decodeURIComponent(
      searchParams.get("email")?.toLowerCase()
    );

    if (emailSearchParam) setKeyValue("_id", emailSearchParam.toLowerCase());
  }, []);

  useEffect(() => {
    setKeyValue("session", uuidv4());
  }, []);

  useEffect(() => {
    if (searchParams.get("jwt")) {
      // abort handling for public url
      setKeyValue("showOverlay", false);
      return;
    }

    setKeyValue("showOverlay", true);
    setKeyValue("isManager", true);

    setUserData().then(data => {
      if (!data?._id) {
        // no manager data responded
        navigate("/login");
        return;
      }

      const organization = isOrganizationBx(data.organization?.name)
        ? "BX"
        : "Level";

      setTenantSpecificHeadAttributes(organization);

      initializePendo(getManagerPendoVisitorMetadata(data), {
        id: getManagerPendoAccountId(data),
      });

      getModelsSaved({ userEmail: data._id })
        .then(models => setKeyValue("listModels", models))
        .catch(showError);

      getModelsSelected({
        organization: data.organization?.name,
        userEmail: data._id,
      })
        .then(etfProducts =>
          setKeyValue("productsList", getMassagedListEtf(etfProducts))
        )
        .catch(showError)
        .finally(() => setKeyValue("showOverlay", false));

      data.managedUsers?.map(it => {
        // notify advisor, if new lead was assigned by admin
        if (
          it?.valueMap?.leadInitial?.isNewLead &&
          it?.valueMap?.leadInitial?.isNotifyAdvisor
        ) {
          api.info({
            message: "A new lead has been added to your Leads page",
            placement: "topRight",
            description: `Lead email: ${it.email}`,
          });

          editLead({
            managerEmail: state._id,
            requestPayload: {
              ...it,
              leadInitial: {
                ...it.valueMap.leadInitial,
                isNewLead: false,
              },
            },
          }).catch(state.showError);
        }
      });
    });
  }, []);

  const setUserData = () => {
    const email =
      state._id ?? decodeURIComponent(searchParams.get("email")?.toLowerCase());

    return getUserMetadata(email)
      .then(userMetadata =>
        getUserManagerData(email)
          .then(userData => {
            setState(lastState => ({
              ...lastState,
              ...userData,
              ...userMetadata,
              managedUsers: userData.managedUsers ?? [],
              loading: false,
            }));

            return {
              ...userData,
              ...userMetadata,
            };
          })
          .catch(error => {
            showError(error);
            setTimeout(() => navigate("/login"), 3000);
          })
      )
      .catch(error => {
        showError(error);
        setTimeout(() => navigate("/login"), 3000);
      });
  };

  const setKeyValue = (key, value) =>
    setState(lastState => ({
      ...lastState,
      [key]: value,
    }));

  const showError = error => {
    console.log(error);
    console.log(typeof error);

    api.error({
      message: getErrorMessageCopy(error),
      placement: "topRight",
      description: "",
    });
  };

  const showSuccess = successMessage =>
    api.success({
      message: successMessage,
      placement: "topRight",
      description: "",
    });

  const showWarning = errorMessage =>
    api.warning({
      message: errorMessage,
      placement: "topRight",
      description: "",
    });

  const mergedState = {
    ...state,
    setKeyValue,
    setUserData,
    showError,
    showSuccess,
    showWarning,
  };

  return (
    <State.Provider value={[mergedState, setState]}>
      <UI
        state={state}
        themeConfig={{
          // static theme tokens
          ...(isOrganizationBx(state.organization?.name)
            ? wealthAdvisorThemeBX
            : wealthAdvisorThemeDefault),
          // Company theme tokens
          ...(state.settings?.theme?.enabled
            ? state.settings.theme.data.tokens
            : {}),
        }}
      >
        <Overlay loading={state.showOverlay}>{children}</Overlay>
      </UI>
      {contextHolder}
    </State.Provider>
  );
};

export default StateProvider;
